import { FC, ReactNode, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { filterConstants } from "../../constants";

import {
  useApiPagedMetaSync,
  useGetPageQueryParams,
  useMerchantApiQueryWithParams,
  useQueryString,
} from "../../hooks";
import {
  IQueryStringNavLinks,
  ITableHeader,
  IViewTransactionListItem,
  IPageAndSearchRequest,
  PagedMeta,
  IPaymentsFilter,
} from "../../interfaces-and-types";
import { View } from "./View";
import { transactionStatus } from "../../enums";
import { SwitchAppButton } from "../../components";

export interface IPaymentQueryMeta {
  status?: string | null;
  filter: IPaymentsFilter;
  start_date?: string | null;
  end_date?: string | null;
}

export interface IPaymentViewProps {
  queryStringTableName: string;
  data: IViewTransactionListItem[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  isCreateNewModalOpen: boolean;
  closeModal: () => void;

  // extra buttons
  onExport: () => void;
  openCreateNewModal: () => void;

  hideDetailsModal: boolean;
  openDetailsModal: (data: any) => void;
}

export const PAYMENTS_LIST_TABLE_NAME = "paymentsTable";

export const Payments: FC<{
  tableName?: string;
  hideColumns?: string[];
  hideQueryStringTabs?: string[];
  defaultQueryStringTab?: string;
  filter?: IPaymentsFilter;
  defaultPageParams?: { page_size?: number; page?: number };
  actions?: (payment: IViewTransactionListItem) => ReactNode;
}> = (props) => {
  const queryStringTableName = props.tableName ?? PAYMENTS_LIST_TABLE_NAME;
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQueryString();
  const status = query.get(
    `${queryStringTableName}.${filterConstants.statusKey}`
  );
  const { page, page_size, search } = useGetPageQueryParams({
    queryStringPrefix: queryStringTableName,
  });

  const startDate = query.get(filterConstants.startDateKey);
  const endDate = query.get(filterConstants.endDateKey);

  const {
    isLoading: isLoadingTransactions,
    isError: isErrorTransactions,
    isSuccess: isSuccessTransactions,
    data: transactions,
    meta: transactionsMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & IPaymentQueryMeta,
    IViewTransactionListItem[],
    PagedMeta
  >([
    "getTransactions",
    {
      page: (page && Number(page)) || 1,
      page_size:
        (page_size && Number(page_size)) ||
        props.defaultPageParams?.page_size ||
        10,
      search,
      status: status || "successful",
      filter: {
        ...props.filter,
        // include_child_parital_payments:
        //   status === transactionStatus.partial ? true : false,
      },
      start_date: startDate,
      end_date: endDate,
    },
  ]);

  useApiPagedMetaSync(transactionsMeta?.page_meta);

  const [isCreateNewModalOpen, setCreateNewModalOpen] = useState(false);
  const closeModal = () => {
    setCreateNewModalOpen(false);
  };

  const headers: ITableHeader[] = [
    {
      key: "amount",
      header: "Amount",
    },
    status !== "successful"
      ? {
          key: "amount_paid",
          header: "Paid",
        }
      : { key: "", header: null },
    {
      key: "status",
      header: "Status",
    },
    {
      key: "reference",
      header: "Reference",
    },
    {
      key: "customer",
      header: "Customer",
    },
    {
      key: "gateway",
      header: "Provider",
    },
    {
      key: "description",
      header: "Description",
    },
    {
      key: "date",
      header: "Date",
    },
    props.actions !== undefined
      ? {
          key: "actions",
          header: "",
        }
      : { key: "", header: null },
    // {
    //   key: "more",
    //   header: "",
    // }
  ];
  const filteredHeaders = !props.hideColumns
    ? headers
    : headers.filter(
        (hd) => !props.hideColumns?.find((col) => col === hd.key.toString())
      );

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.statusKey,
    options: [
      // {
      //   key: "All",
      //   value: null,
      // },
      {
        key: "Successful",
        value: "successful",
        description: (
          <>
            Congrats! SwitchApp has{" "}
            <strong className="text-success">
              received the payment in FULL
            </strong>{" "}
            from your customer. Click the payment to see more details.
          </>
        ),
      },
      {
        key: "Partial",
        value: "partial",
        description: (
          <>
            SwitchApp has{" "}
            <span className="text-warning">
              received a PARTIAL PAYMENT of the total amount
            </span>
            . Your customer may complete the payment at a later time.
          </>
        ),
      },
      {
        key: "Processing",
        value: "processing",
        description: (
          <>
            We're still <span className="text-default">waiting</span> for your
            customer to make a payment.{" "}
            <span className="text-warning">NOTE:</span>. If your customer says
            they have already made payment, please visit the{" "}
            <strong className="text-warning">PAYMENT ISSUES TAB above </strong>
            to view the payments that have been rejected due to 'INCOMPLETE
            AMOUNT SENT'
          </>
        ),
      },
      {
        key: "Refunded",
        value: "refunded",
        description: (
          <>
            These payments were{" "}
            <strong className="text-warning">
              initially received as SUCCESSFUL
            </strong>
            , but have been returned to the customer for various reasons e.g.
            'Product/Service not received'.
            <strong className="text-warning"> NOTE:</strong> This is not the
            same as a REJECTED PAYMENT due to 'INCOMPLETE AMOUNT SENT'. Please
            visit the{" "}
            <strong className="text-warning">PAYMENT ISSUES TAB above </strong>
            to view rejected payments
          </>
        ),
      },
      {
        key: "Abandoned",
        value: "abandoned",
        description: (
          <>
            Oh dear! Your customer{" "}
            <strong className="text-warning">
              didn't make the payment within the allowed period
            </strong>
            . The link to complete the payment is no longer available too.
            Please inform them to restart the payment process.
          </>
        ),
      },
      {
        key: "Failed",
        value: "failed",
        description: (
          <>
            Gremlins in the internet wheels!
            <br /> Your customers{" "}
            <strong className="text-warning">
              tried their best to pay you, but were unable to complete the
              payment
            </strong>
            . Please inform them to try again.
          </>
        ),
      },
    ],
  };

  const filteredQueryStringTabs = !props.hideQueryStringTabs
    ? queryStringTabs
    : {
        ...queryStringTabs,
        options: queryStringTabs.options.filter(
          (qryOpt) =>
            !props.hideQueryStringTabs?.find(
              (hiddenTab) => hiddenTab === qryOpt.value.toString()
            )
        ),
      };

  const onExport = () => {
    alert("exporting data...");
  };

  const openCreateNewModal = () => {
    setCreateNewModalOpen(true);
  };

  const openDetailsModal = (id: any) => {
    navigate({
      pathname: `${id}/details`,
      search: location.search,
    });
  };

  // insert the functions to open edit/delete modals
  const paymentsWithActions: IViewTransactionListItem[] = !props.actions
    ? transactions || []
    : transactions?.map((tx) => ({
        ...tx,

        // add actions
        actions: props.actions && props.actions(tx),
      })) || [];

  return (
    <View
      queryStringTableName={queryStringTableName}
      data={paymentsWithActions}
      headers={filteredHeaders}
      isLoading={isLoadingTransactions}
      isError={isErrorTransactions}
      totalCount={transactionsMeta?.page_meta.item_count || 0}
      queryStringTabs={filteredQueryStringTabs}
      isCreateNewModalOpen={isCreateNewModalOpen}
      onExport={onExport}
      openCreateNewModal={openCreateNewModal}
      closeModal={closeModal}
      hideDetailsModal={!!props.actions}
      openDetailsModal={openDetailsModal}
    />
  );
};
